import { ChangeEventHandler, FC, FormEventHandler, useRef, useState } from 'react';

import { CloudUpload } from 'lucide-react';
import { isEmpty, isNonNullish, randomString } from 'remeda';
import { toast } from 'sonner';
import { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { FileContentTypeIcon } from '../../../../../fileContentTypeIcon/FileContentTypeIcon';

import { SUPPORTED_FILE_TYPES } from './constants';
import { UploadedFile } from './types';

import css from './UploadFilesDialog.module.css';

import { fileSystemService } from 'v2/api/fileSystemService';
import { FolderModel } from 'v2/types/FileSystemModel';
import { handleHttpError } from 'v2/utils';

interface UploadFilesDialogProps {
  folder: FolderModel;
  isOpen: boolean;
  onClose: VoidFunction;
}

export const UploadFilesDialog: FC<UploadFilesDialogProps> = ({ folder, isOpen, onClose }) => {
  const ref = useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<UploadedFile[]>([]);

  const { trigger, isMutating } = useSWRMutation(
    ['uploadFilesToFolder', folder.id],
    fileSystemService.uploadFilesToFolder,
    {
      onSuccess: () => {
        mutate(['getChildFiles', folder.id]);
        toast.success('Files have been successfully uploaded');
        handleClose();
      },
      onError: handleHttpError,
    }
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (isNonNullish(e.target.files)) {
      for (let i = 0; i < e.target.files.length; i++) {
        const file = e.target.files[i];
        const reader = new FileReader();

        reader.onloadend = () => {
          setFiles((prev) => [
            ...prev,
            {
              id: randomString(4),
              src: reader.result,
              file,
            },
          ]);
        };

        if (e.target.files[i]) {
          reader.readAsDataURL(file);
        }
      }
    }
  };

  const handleClose = () => {
    if (ref.current) {
      ref.current.value = '';
    }

    setFiles([]);
    onClose();
  };

  const handleDelete = (fileId: string) => () => {
    setFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const formData = new FormData();
    files.forEach((item) => formData.append('file[]', item.file));

    trigger(formData);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Upload Files</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent className="form">
          <div className={css.uploadFilesFormCardContainer}>
            <div className={css.uploadFilesFormCard}>
              <input
                ref={ref}
                type="file"
                accept={SUPPORTED_FILE_TYPES.join(',')}
                className={css.uploadFilesFormInput}
                onChange={handleChange}
                multiple
              />

              <div className={css.uploadFilesFormHintContainer}>
                <CloudUpload width={40} height={40} />

                <p>Drag and drop or click to upload</p>
              </div>
            </div>

            <Alert severity="info">Supported file formats: {SUPPORTED_FILE_TYPES.join(', ')}</Alert>
          </div>

          {!isEmpty(files) && (
            <div className={css.uploadFilesList}>
              {files.map(({ id, file, src }) => (
                <div key={id} className={css.uploadedFileCard}>
                  <div className={css.uploadedFileImageContainer}>
                    {file.type.includes('image') ? (
                      <img className={css.uploadedFileImage} src={src as string} alt={file.name} />
                    ) : (
                      <FileContentTypeIcon contentType={file.type} />
                    )}
                  </div>

                  <div className={css.uploadedFileDetails}>
                    <p className={css.uploadedFileName}>{file.name}</p>

                    <Button
                      className={css.uploadFilesFormButton}
                      onClick={handleDelete(id)}
                      color="error"
                      size="small"
                      variant="outlined"
                      type="button"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>

        <DialogActions>
          <Button type="button" onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button type="submit" variant="contained" loading={isMutating}>
            Upload
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
